import { useEffect, useState } from "react";
import { ExternalLink, Globe, ShieldCheck, Headset } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { WhatsAppFloat } from "@/components/WhatsAppFloat";
import { useSettings } from "@/context/SettingsContext";

export default function DmcHubPage() {
  const settings = useSettings();
  const portalUrl = settings.dmc_hub_url || "https://dmchub.emergent.host";
  const [loaded, setLoaded] = useState(false);
  const [slowHint, setSlowHint] = useState(false);

  useEffect(() => {
    setLoaded(false);
    setSlowHint(false);
    // iframes that hit a browser error page still fire onLoad — after 12s show
    // a reachable-anyway hint instead of leaving a blank white panel unexplained
    const t = setTimeout(() => setSlowHint(true), 12000);
    return () => clearTimeout(t);
  }, [portalUrl]);

  return (
    <div className="min-h-screen bg-ink flex flex-col" data-testid="dmc-hub-page">
      <Navbar />

      <section className="pt-28 pb-8 px-6">
        <div className="max-w-6xl mx-auto">
          <p className="font-mono text-[11px] tracking-[0.35em] uppercase text-gold mb-3">Partner Access</p>
          <div className="flex flex-wrap items-end justify-between gap-4">
            <h1 className="font-display text-4xl sm:text-5xl text-cream" data-testid="dmc-hub-title">
              DMC Hub
            </h1>
            <a
              href={portalUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="dmc-hub-open-newtab"
              className="flex items-center gap-2 text-xs font-mono tracking-widest uppercase text-gold border border-gold/40 px-5 py-2.5 rounded-full hover:bg-gold hover:text-ink transition-colors"
            >
              Open in New Tab <ExternalLink size={14} />
            </a>
          </div>
          <p className="text-mist text-sm mt-3 max-w-2xl">
            The {settings.company_name} partner portal — net rates, markups, bookings and vouchers for registered DMC partners.
          </p>
          <div className="flex flex-wrap gap-x-8 gap-y-2 mt-5">
            {[
              { icon: Globe, text: "Live B2B inventory" },
              { icon: ShieldCheck, text: "Secure partner login" },
              { icon: Headset, text: "Dedicated partner support" },
            ].map((f) => (
              <span key={f.text} className="flex items-center gap-2 text-xs text-faded">
                <f.icon size={14} className="text-gold" /> {f.text}
              </span>
            ))}
          </div>
        </div>
      </section>

      <section className="px-6 pb-16 flex-1 flex flex-col">
        <div className="max-w-6xl mx-auto w-full flex-1 flex flex-col">
          <div className="relative flex-1 min-h-[70vh] rounded-2xl overflow-hidden border border-gold/20 bg-card">
            {!loaded && (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-3" data-testid="dmc-hub-loading">
                <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" />
                <p className="text-xs text-faded font-mono tracking-widest uppercase">Loading partner portal…</p>
              </div>
            )}
            <iframe
              src={portalUrl}
              title={`${settings.company_name} DMC Hub — Partner Portal`}
              data-testid="dmc-hub-iframe"
              onLoad={() => setLoaded(true)}
              className="w-full h-full min-h-[70vh] bg-white"
              allow="clipboard-read; clipboard-write"
            />
          </div>
          {slowHint && (
            <div className="mt-4 flex items-center justify-between gap-4 rounded-xl border border-gold/30 bg-gold/10 px-5 py-3" data-testid="dmc-hub-slow-hint">
              <p className="text-xs text-cream/80">Portal taking too long or showing a blank page? It works best in its own tab.</p>
              <a href={portalUrl} target="_blank" rel="noopener noreferrer" className="text-xs font-semibold text-gold hover:underline shrink-0" data-testid="dmc-hub-slow-hint-link">
                Open in new tab ↗
              </a>
            </div>
          )}
          <p className="text-center text-[11px] text-faded mt-4">
            Trouble viewing the portal?{" "}
            <a href={portalUrl} target="_blank" rel="noopener noreferrer" className="text-gold hover:underline" data-testid="dmc-hub-fallback-link">
              Open it in a new tab
            </a>
          </p>
        </div>
      </section>

      <Footer />
      <WhatsAppFloat />
    </div>
  );
}
